import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  LoadingOverlay,
  Drawer as MantineDrawer,
  Tabs as MantineTabs,
  Menu,
  px,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconExclamationCircle } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import dayjs from 'dayjs';
import useAppointment from 'hooks/appointment/useAppointment';
import useUpdateAppointment from 'hooks/appointment/useUpdateAppointment';
import { calculateEventEndDateTime } from 'utils/event.helpers';

import DetailsEditAllFutureAppointmentsForm from './components/DetailsEditAllFutureAppointmentsForm';
import DetailsEditSelectedDayForm from './components/DetailsEditSelectedDayForm';
import DetailsView from './components/DetailsView';
import ModalAppointmentDelete from './components/ModalAppointmentDelete';
import ReservationsForm from './components/ReservationsForm';
import { DragPrefill } from '../../hooks/useEventDragUpdate';
import Footer from '../Footer';
import NotesView from '../NotesView';
import { EventDrawerState } from '../types';

interface IEditAppointmentDrawerProps {
  opened: boolean;
  closeHandler: () => void;
  setAppointmentId: React.Dispatch<React.SetStateAction<string | undefined>>;
  appointmentId?: string;
  permission?: boolean;
  dragPrefill?: DragPrefill;
}

const EditAppointmentDrawer = ({
  opened,
  closeHandler,
  setAppointmentId,
  appointmentId,
  permission = true,
  dragPrefill,
}: IEditAppointmentDrawerProps): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, true);
  const { data, isLoading, isFetching, isSuccess, isError } = useAppointment(appointmentId);
  const [state, setState] = useState<EventDrawerState>(dragPrefill ? 'edit-all-future-events' : 'view');
  const [activeTab, setActiveTab] = useState<string | null>(dragPrefill ? 'details' : 'reservations');
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const updateMutation = useUpdateAppointment();

  const loader = useMemo(
    () => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader />
      </div>
    ),
    [],
  );

  const handleCloseAndStateReset = useCallback((): void => {
    closeHandler();
    setState('view');
  }, [closeHandler]);

  const detailsComponent = useMemo(() => {
    switch (state) {
      case 'view':
        return data ? <DetailsView appointment={data.data} /> : loader;
      case 'edit-selected-day':
        return data ? (
          <DetailsEditSelectedDayForm
            appointment={data.data}
            closeHandler={handleCloseAndStateReset}
            formId={`${state}-appointment-form`}
            updateMutation={updateMutation}
          />
        ) : (
          loader
        );
      case 'edit-all-future-events':
        return data ? (
          <DetailsEditAllFutureAppointmentsForm
            appointment={data.data}
            closeHandler={handleCloseAndStateReset}
            formId={`${state}-appointment-form`}
            updateMutation={updateMutation}
            prefillStartTime={dragPrefill?.startTime}
            prefillOriginalCoachId={dragPrefill?.originalCoachId}
            prefillNewCoachId={dragPrefill?.newCoachId}
            prefillNewCoachName={dragPrefill?.newCoachName}
          />
        ) : (
          loader
        );
      default:
        return <></>;
    }
  }, [
    state,
    data,
    loader,
    handleCloseAndStateReset,
    updateMutation,
    dragPrefill?.startTime,
    dragPrefill?.originalCoachId,
    dragPrefill?.newCoachId,
    dragPrefill?.newCoachName,
  ]);

  const reservationsFormComponent = useMemo(() => {
    if (isLoading) {
      return loader;
    }
    if (isSuccess && data) {
      return (
        <ReservationsForm
          appointment={data.data}
          permission={permission}
          handleCloseDrawer={handleCloseAndStateReset}
        />
      );
    }
    return <></>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching, isLoading, isSuccess, loader]);

  const notesComponent = useMemo(() => {
    if (isLoading) {
      return loader;
    }
    if (isSuccess && data) {
      return <NotesView athlete_kilo_classes_or_appointments={data.data.athleteAppointments} />;
    }
    return <></>;
  }, [data, isLoading, isSuccess, loader]);

  const tabs = useMemo(() => {
    const defaultTabs = [
      {
        label: 'Reservations',
        value: 'reservations',
        component: reservationsFormComponent,
        tabProps: { 'data-test': 'reservations' },
      },
      {
        label: 'Details',
        value: 'details',
        component: detailsComponent,
        tabProps: { 'data-test': 'details' },
      },
      {
        label: 'Notes',
        value: 'notes',
        component: notesComponent,
      },
    ];
    return defaultTabs;
  }, [detailsComponent, notesComponent, reservationsFormComponent]);

  const headerData =
    data &&
    (isDesktop ? (
      <Group sx={{ gap: theme.spacing.xl }}>
        {dayjs(data.data.startDate).format('MMMM D, YYYY')} at{' '}
        {dayjs(`${data.data.startDate} ${data.data.startTime}`).format('h:mm A')} -{' '}
        {calculateEventEndDateTime(
          data.data.duration,
          data.data.startTime,
          dayjs(data.data.startDate).toDate(),
        ).format('h:mm A')}
      </Group>
    ) : (
      <div>
        {dayjs(data.data.startDate).format('MMMM D, YYYY')}
        <Group>
          {dayjs(`${data.data.startDate} ${data.data.startTime}`).format('h:mm A')} -{' '}
          {calculateEventEndDateTime(
            data.data.duration,
            data.data.startTime,
            dayjs(data.data.startDate).toDate(),
          ).format('h:mm A')}
        </Group>
      </div>
    ));

  if (isError) {
    return (
      <Center p='lg'>
        <Alert icon={<IconExclamationCircle size={px(theme.fontSizes.md)} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      </Center>
    );
  }
  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndStateReset}
      size={670}
      title={<Title order={3}>{data?.data.appointmentType.name}</Title>}
      padding='0'
      closeOnClickOutside
      footer={
        <Footer
          state={state}
          setState={setState}
          closeHandler={handleCloseAndStateReset}
          formId={`${state}-appointment-form`}
          loadingSubmit={updateMutation.isLoading || isLoading || isFetching}
        />
      }
      closeButtonSection={
        <Group>
          {headerData}
          <MantineDrawer.CloseButton />
        </Group>
      }
    >
      <LoadingOverlay visible={isLoading} />
      <MantineTabs value={activeTab} onTabChange={setActiveTab}>
        <MantineTabs.List p='xl' pb={0}>
          {tabs.map(item => (
            <MantineTabs.Tab {...item.tabProps} key={item.value} value={item.value}>
              {item.label}
            </MantineTabs.Tab>
          ))}
          <Menu position='bottom-end'>
            <Menu.Target>
              <Button
                compact
                disabled={state != 'view' || !permission}
                ml='auto'
                mt='-.5rem'
                data-cy='actionsBtn'
              >
                Actions
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                data-cy='editThisDayOnlyBtn'
                onClick={() => {
                  setState('edit-selected-day');
                  setActiveTab('details');
                }}
              >
                Edit this day only
              </Menu.Item>
              {data && dayjs(`${data.data.startDate} ${data.data.startTime}`).isAfter(dayjs(new Date())) && (
                <Menu.Item
                  onClick={() => {
                    setState('edit-all-future-events');
                    setActiveTab('details');
                  }}
                >
                  Edit all future events
                </Menu.Item>
              )}
              <Menu.Item data-cy='deleteEventBtn' onClick={openDeleteModal}>
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </MantineTabs.List>
        {tabs.map(item => (
          <MantineTabs.Panel key={item.value} value={item.value} p='xl'>
            {item.component}
          </MantineTabs.Panel>
        ))}
      </MantineTabs>
      <ModalAppointmentDelete
        appointmentId={appointmentId}
        opened={deleteModalOpened}
        handleCloseModal={closeDeleteModal}
        handleCloseDrawer={handleCloseAndStateReset}
        setAppointmentId={setAppointmentId}
      />
    </Drawer>
  );
};

export default EditAppointmentDrawer;
